import C15_SocialSharing from "@components/C15_SocialSharing/C15_SocialSharing";
import Grid from "@components/Grid/Grid";
import { NextContentfulImage } from "@components/Image";
import Link from "@components/Link/Link";
import StructuredData from "@components/StructuredData/StructuredData";
import { SearchLandingType } from "@customTypes/SearchLandingType";
import { ImageWrapperType } from "@customTypes/imageWrapper";
import { useSettings } from "@utilities/context/settings";
import { useDevice } from "@utilities/react/get-device/get-device";
import RenderComponents from "@utilities/renderComponents";
import clsx from "clsx";
import { format, formatISO, parseISO } from "date-fns";
import Head from "next/head";

import { getTopicIdAndName } from "@components/CoveoSearchComponent/coveoUtils";
import { getContentfullImagePath } from "@utilities/getContentfullImagePath";
import styles from "../article.module.scss";

export interface NewsDetailProps {
  newsTitle: string;
  shortDescription?: string;
  showShortDescription?: boolean;
  date: string;
  bodyContent?: any;
  thumbnailImage?: ImageWrapperType;
  articleImage?: ImageWrapperType;
  tags?: any;
  newsTopic?: string[];
  basePageTemplate: {
    fields: {
      pageTitle: string;
    };
  };
}

export const NewsDetail = (props: { content: NewsDetailProps }) => {
  const { content } = props;
  const {
    shortDescription,
    showShortDescription,
    newsTitle,
    date,
    bodyContent,
    thumbnailImage,
    articleImage,
    tags,
    newsTopic,
    basePageTemplate: {
      fields: { pageTitle },
    },
  } = content;

  const { siteSettings } = useSettings();
  const device = useDevice();
  const isMobile = device === "mobile";

  const searchPage = siteSettings?.searchResultsPagesCollection?.items?.filter(
    ({ type }) => type === SearchLandingType.NewsTopics
  )[0];

  const formattedDate = formatISO(date);

  const schemaData = {
    "@type": "NewsArticle",
    headline: pageTitle,
    image: `https:${thumbnailImage?.fields.url}`,
    datePublished: formattedDate,
    dateModified: formattedDate,
    author: {
      "@type": "Organization",
      name: "London Business School",
      url: "https://www.london.edu",
    },
    publisher: {
      "@type": "Organization",
      name: "London Business School",
      logo: {
        "@type": "ImageObject",
        url: "https://images.london.edu/hxo16fanegqh/4KD0tsxILYEZKUprJxltZT/127b23210dfc15b641376e29117796a5/lbs-logo.png",
      },
    },
    description: `${shortDescription}`,
  };

  return (
    <>
      <Head>
        <meta name="articledateyear" content={format(parseISO(date), "yyyy")} />
        <meta
          name="articledate"
          content={format(parseISO(date), "yyyy/MM/dd")}
        />
        <meta
          name="articledatetext"
          content={format(parseISO(date), "dd MMMM yyyy")}
        />
        {newsTopic && (
          <meta
            name="newstopic"
            content={newsTopic.map((topic) => topic).join(";")}
          />
        )}
        <meta
          name="articledatemonth"
          content={format(parseISO(date), "MMMM")}
        />
        <meta name="newstitle" content={newsTitle} />
        <meta name="ShortDescription" content={shortDescription} />
        {!!thumbnailImage && (
          <>
            <meta
              key="articleimagealt"
              name="articleimagealt"
              content={thumbnailImage?.fields.altText}
            />
            <meta
              key="articleimageurl"
              name="articleimageurl"
              content={
                getContentfullImagePath(thumbnailImage?.fields?.url) || ""
              }
            />
            <meta
              key="twitter:image"
              name="twitter:image"
              content={`${thumbnailImage?.fields.url}?w=1200&h=1200`}
            />
            <meta
              key="og:image"
              property="og:image"
              content={`${thumbnailImage?.fields.url}?w=1200&h=600`}
            />
          </>
        )}
      </Head>
      <div className="wrapper">
        <Grid row>
          <Grid column sm={12}>
            <section className={clsx("component", styles["article-header"])}>
              <div className="wrapper">
                <div>
                  <h1>{pageTitle}</h1>
                  {showShortDescription && shortDescription && (
                    <p>{shortDescription}</p>
                  )}
                </div>
                <div
                  className={clsx(
                    styles["article-details-wrapper"],
                    "flex",
                    "justify-between",
                    "items-start",
                    "gap-4"
                  )}
                >
                  <div className={styles["article-details"]}>
                    <div className={styles["date-read-time"]}>
                      <span className={styles.date}>
                        {format(parseISO(date), "dd MMMM yyyy")}
                      </span>
                    </div>
                  </div>
                  {!isMobile && (
                    <C15_SocialSharing
                      socialLinks={siteSettings?.globalSocialShareCollection}
                      isSocialIconDark
                      small
                      mobileAlignment
                    />
                  )}
                </div>
                <div
                  className={clsx("component", "tag", styles.tag, "md:w-2/3")}
                >
                  {Object.keys(tags).map((key) => {
                    const [, tagName] = getTopicIdAndName(tags[key]);
                    return (
                      <Link
                        key={`newsTopic-${tagName}`}
                        href={`/${searchPage?.url?.slug}#f:${searchPage?.queryParams}=[${tagName}]`}
                      >
                        {tagName}
                      </Link>
                    );
                  })}
                </div>
                {isMobile && (
                  <C15_SocialSharing
                    socialLinks={siteSettings?.globalSocialShareCollection}
                    isSocialIconDark
                    small
                    mobileAlignment
                  />
                )}
              </div>
            </section>
            {articleImage && (
              <section
                className={clsx(
                  "component",
                  styles["article-images"],
                  "full-width"
                )}
              >
                <NextContentfulImage
                  src={articleImage.fields.url}
                  alt={articleImage.fields.altText || ""}
                  width={1140}
                  height={346}
                  preload
                  fetchPriority="high"
                />
              </section>
            )}
          </Grid>
        </Grid>
      </div>
      {RenderComponents(bodyContent)}
      <StructuredData id="newsSiteMeta" schemaData={schemaData} />
    </>
  );
};

export default NewsDetail;
